import PropTypes from "prop-types";
import React from "react";

import { window } from "@swa-ui/browser/window";

import { useHref } from "../useHref/useHref";

/**
 * Higher-order component that wraps a given link component with encrypted URL functionality.
 * This HOC encrypts the provided data using JWE, appends it as a query parameter to the base URL,
 * and merges any additional search params before passing the final href to the wrapped component.
 *
 * @param {React.ComponentType} Component - The link component to wrap
 * @param {object} props - Configuration for the encrypted link
 * @param {object} [props.additionalUrlSearchParams={}] - Additional URL search parameters to append
 * @param {string} props.componentName - Name of the consuming component for logging purposes
 * @param {object} props.dataToEncrypt - Data to encrypt and include as a query parameter
 * @param {string} props.feature - Feature key used to resolve the JWKS config for encryption
 * @param {object} props.linkProps - Props to spread onto the wrapped component
 * @param {string} props.url - Base URL path for the link
 * @param {React.ReactNode} props.children - Content rendered inside the wrapped component
 * @returns {React.Element} The wrapped component with an encrypted href
 */
export function encryptedLink(Component, props) {
  const {
    additionalUrlSearchParams = {},
    componentName,
    dataToEncrypt,
    feature,
    linkProps,
    url,
  } = props;
  let { href } = useHref(dataToEncrypt, url, componentName, feature);

  return <Component {...getLinkProps()}>{props.children}</Component>;

  function getLinkProps() {
    return {
      ...linkProps,
      href: getLinkHref(),
    };
  }

  function getLinkHref() {
    href = new URL(href, window.location.origin);

    href.search = new URLSearchParams({
      ...Object.fromEntries(href.searchParams),
      ...additionalUrlSearchParams,
    }).toString();

    return href.toString();
  }
}

encryptedLink.propTypes = {
  /** Additional URL search parameters. */
  additionalUrlSearchParams: PropTypes.object,

  /** Content to be rendered inside the component. */
  children: PropTypes.node.isRequired,

  /** Name of the component. */
  componentName: PropTypes.string.isRequired,

  /** Data to encrypt and include in the URL. */
  dataToEncrypt: PropTypes.object.isRequired,

  /** Feature name related to the encrypted link. */
  feature: PropTypes.string.isRequired,

  /** Link props to pass to the component. */
  linkProps: PropTypes.object.isRequired,

  /** Base URL */
  url: PropTypes.string.isRequired,
};
